import { useCallback, useState } from 'react';

export type BackgroundMode =
  | 'default'
  | 'black'
  | 'white'
  | 'gray'
  | 'custom'
  | 'image';

const PRESET_COLORS: Record<'black' | 'white' | 'gray', string> = {
  black: '#000000',
  white: '#ffffff',
  gray: '#888888'
};

function useModelViewerBackground() {
  const [backgroundMode, setBackgroundMode] =
    useState<BackgroundMode>('default');
  const [customColor, setCustomColorState] = useState('#ffffff');
  const [backgroundImageUrl, setBackgroundImageUrlState] = useState<
    string | null
  >(null);
  /** S3 URLs uploaded during this viewer session — persisted across modal opens */
  const [sessionImageUrls, setSessionImageUrls] = useState<string[]>([]);

  const setPresetBackground = useCallback(
    (mode: 'default' | 'black' | 'white' | 'gray') => {
      setBackgroundMode(mode);
    },
    []
  );

  const setCustomColor = useCallback((hex: string) => {
    setBackgroundMode('custom');
    setCustomColorState(hex);
  }, []);

  const setBackgroundImage = useCallback((url: string) => {
    setBackgroundMode('image');
    setBackgroundImageUrlState(url);
  }, []);

  const addSessionImageUrl = useCallback((url: string) => {
    setSessionImageUrls((prev) => (prev.includes(url) ? prev : [url, ...prev]));
  }, []);

  const removeSessionImageUrl = useCallback((url: string) => {
    setSessionImageUrls((prev) => prev.filter((u) => u !== url));
  }, []);

  let backgroundCssValue = '';
  if (backgroundMode === 'black') backgroundCssValue = PRESET_COLORS.black;
  else if (backgroundMode === 'white') backgroundCssValue = PRESET_COLORS.white;
  else if (backgroundMode === 'gray') backgroundCssValue = PRESET_COLORS.gray;
  else if (backgroundMode === 'custom') backgroundCssValue = customColor;
  else if (backgroundMode === 'image' && backgroundImageUrl)
    backgroundCssValue = `url("${backgroundImageUrl}") center/contain no-repeat`;

  return {
    backgroundMode,
    customColor,
    backgroundImageUrl,
    backgroundCssValue,
    sessionImageUrls,
    setPresetBackground,
    setCustomColor,
    setBackgroundImage,
    addSessionImageUrl,
    removeSessionImageUrl
  };
}

export default useModelViewerBackground;
